import { useCookies } from 'react-cookie';
import { useQuery } from 'react-query';
import { jwtDecode } from 'jwt-decode';
import { useAppSwitcherFlag } from '../flags';
import { fetchWrapper } from '../utils';
import { AppSwitcherData } from './useAppSwitcherDataTypes';

export const ZASTokenCookie = 'ZASTOKEN';
export const ZuoraCurrentEntity = 'ZuoraCurrentEntity';

export const ONE_ID_STG = 'one.stg.na.zuora.com';
export const ONE_ID_PROD = 'one.zuora.com';
const STAGING_HOSTS = [
  'staging2.zuora.com',
  'one.stg.na.zuora.com',
  'experiences-staging2.zuora.com',
];
// ONE_ID_STG for development purposes
export const ONE_ID_HOST = STAGING_HOSTS.includes(window.location.hostname)
  ? ONE_ID_STG
  : ONE_ID_PROD;

/**
 * Resolves the OneID base URL (including scheme) to send the AppSwitcher
 * request to. The ZASTOKEN is only valid against the environment that issued
 * it, and it carries that environment's origin in its `URI` claim (e.g.
 * `https://one.preprod.zuora.com`). Deriving the host from the token means
 * every environment (preprod, sandbox, EU/AP, future ones) is handled
 * automatically instead of relying on a hostname allowlist that silently
 * defaults everything to production.
 *
 * Falls back to the hostname-based `ONE_ID_HOST` when there is no token or it
 * cannot be decoded (e.g. Storybook/tests set the cookie to a non-JWT value).
 */
export const getOneIdBaseUrl = (zastoken?: unknown): string => {
  const fallback = `https://${ONE_ID_HOST}`;
  if (typeof zastoken !== 'string' || !zastoken) {
    return fallback;
  }
  try {
    const { URI } = jwtDecode<{ URI?: string }>(zastoken);
    return URI || fallback;
  } catch (e) {
    console.warn(
      'Unable to parse ZASTOKEN; falling back to default OneID host',
      e,
    );
    return fallback;
  }
};

/**
 *
 * Returns the AppSwitcher data and a isEnabled boolean value that means a ZASToken header is present and the AppSwitcher Flag is enabled
 * alongside error and isLoading values
 */
export const useAppSwitcherData = () => {
  const [isAppSwitcherEnabled] = useAppSwitcherFlag();
  const [cookies] = useCookies([ZASTokenCookie]);

  const { data, error, isLoading } = useQuery(
    'AppSwitcherData',
    async () => {
      const baseUrl = getOneIdBaseUrl(cookies.ZASTOKEN);
      return fetchWrapper<AppSwitcherData>(
        `${baseUrl}/api/appswitcher/navigation`,
        {
          headers: {
            [ZASTokenCookie]: cookies.ZASTOKEN,
          },
        },
      );
    },
    {
      enabled: !!cookies.ZASTOKEN && isAppSwitcherEnabled,
    },
  );

  return {
    data,
    error,
    isLoading,
    isEnabled: !!cookies.ZASTOKEN && isAppSwitcherEnabled,
  };
};
